import React from 'react';
import SEO from '../components/SEO';
import './LGBTIQ.css';
import { Link } from 'react-router-dom';

function LGBTIQ() {
  return (
    <div className="lgbtiq-page">
      <SEO 
        title="LGBTIQA+ Affirming Psychologist Australia | Gender-Affirming Therapy | Clinical Minds"
        description="LGBTIQA+ affirming and gender-affirming psychology services. Safe, confidential support for identity, relationships, substance use, and mental health. Telehealth across Australia. Published researcher in LGBTIQA+ mental health."
        canonicalUrl="https://www.clinicalminds.net/lgbtqia"
        noindex={true}
      />
      {/* Header */}
      <div className="lgbtiq-header">
        <div className="lgbtiq-logo">
          <img 
            src="/images/logos/clinical-minds-logo-white.png" 
            alt="Clinical Minds Psychology" 
          />
        </div>
      </div>

      {/* Hero Section */}
      <div className="lgbtiq-hero">
        <div className="lgbtiq-hero-overlay"></div>
        <div className="lgbtiq-hero-content">
          <div className="lgbtiq-pride-accent"></div>
          <h1>LGBTIQA+ Affirming Psychology</h1>
          <p className="lgbtiq-subtitle">Safe, authentic, and experienced mental health support for our community</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="lgbtiq-container">
        
        {/* Welcome Section */}
        <section className="lgbtiq-section lgbtiq-welcome">
          <div className="lgbtiq-welcome-inner">
            <div className="lgbtiq-profile">
              <img 
                src="/images/photos/lachlan-headshot.jpg" 
                alt="Lachlan Beaton, Clinical Psychologist (Registrar)"
                className="lgbtiq-profile-image"
              />
            </div>
            <div className="lgbtiq-welcome-content">
              <h2>Lachlan Beaton <span className="lgbtiq-pronouns">(he/him)</span></h2>
              <p className="lgbtiq-title">Clinical Psychologist (Registrar)</p>
              
              <p className="lgbtiq-intro-text">
                I provide affirming, trauma-informed psychological services specifically for the LGBTIQA+ community. 
                As an ally and advocate who has worked extensively within our community, I deeply understand the unique 
                challenges we face, from minority stress and discrimination to navigating identity, relationships, and acceptance.
              </p>
              
              <p className="lgbtiq-intro-text">
                Whether you're exploring your identity, managing mental health, working through substance use concerns, 
                or healing from trauma, I offer a genuinely safe, non-judgmental space where you can be authentically yourself. 
                Your story matters, and you deserve support that truly understands your lived experience.
              </p>

              <div className="lgbtiq-credentials">
                <div className="lgbtiq-credential-section">
                  <h3>Qualifications</h3>
                  <ul>
                    <li>Master of Psychology (Clinical)</li>
                    <li>Bachelor of Psychological Science (Honours)</li>
                    <li>Bachelor of Commerce, Diploma of Arts (Political Science), CPA</li>
                    <li>AHPRA Registered • PSY0002785698 • MAPS</li>
                  </ul>
                </div>

                <div className="lgbtiq-credential-section">
                  <h3>Areas of Focus</h3>
                  <ul>
                    <li>LGBTIQA+ affirming and gender-affirming practice</li>
                    <li>Anxiety, depression, and trauma</li>
                    <li>Alcohol and substance use concerns</li>
                    <li>Identity, relationships, and family dynamics</li>
                  </ul>
                </div>

                <div className="lgbtiq-credential-section">
                  <h3>Specific LGBTIQA+ Experience</h3>
                  <ul>
                    <li><strong>Trans & Non-Binary Affirming:</strong> Experience with gender-affirming care, pronoun use, and supporting transition journeys</li>
                    <li><strong>Diverse Identities:</strong> Working with gay, lesbian, bisexual, pansexual, asexual, aromantic, and questioning clients</li>
                    <li><strong>Relationship Diversity:</strong> Understanding of polyamorous, open relationships, and diverse relationship structures</li>
                    <li><strong>Substance Use Experience:</strong> Published author on alcohol and drug addiction within LGBTIQA+ contexts and minority stress</li>
                    <li><strong>Marriage Equality Advocate:</strong> Long-time supporter of LGBTIQA+ rights, equality, and social justice</li>
                    <li><strong>Intersectionality Aware:</strong> Understanding of how race, disability, culture, and other identities intersect with LGBTIQA+ experiences</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Expertise Section */}
        <section className="lgbtiq-section lgbtiq-expertise">
          <div className="lgbtiq-content">
            <h2>Affirming Experience</h2>
            <p className="lgbtiq-section-subtitle">Evidence-based practice with an informed understanding of LGBTIQA+ mental health</p>
            
            <div className="lgbtiq-expertise-grid">
              <div className="lgbtiq-expertise-card">
                <div className="lgbtiq-card-icon">🏳️‍🌈</div>
                <h3>Affirming Practice</h3>
                <p>Gender-affirming care that respects your pronouns, identity, and lived experience. Whether you're questioning, transitioning, or simply being yourself, you're welcomed exactly as you are.</p>
              </div>

              <div className="lgbtiq-expertise-card">
                <div className="lgbtiq-card-icon">🧠</div>
                <h3>Understanding Minority Stress</h3>
                <p>Knowledge of how discrimination, rejection, and microaggressions impact mental health. I understand that your struggles aren't just "anxiety" - they're often responses to a world that hasn't always been safe.</p>
              </div>

              <div className="lgbtiq-expertise-card">
                <div className="lgbtiq-card-icon">💊</div>
                <h3>Substance Use Experience</h3>
                <p>Published researcher in addiction with specific understanding of substance use in LGBTIQA+ contexts: party culture, chemsex, self-medication, and healing without judgment.</p>
              </div>

              <div className="lgbtiq-expertise-card">
                <div className="lgbtiq-card-icon">🔒</div>
                <h3>Confidentiality</h3>
                <p>Secure telehealth across Australia. Your privacy is protected: billing is discreet, sessions are confidential, and you control what's shared. Confidentiality has the usual legal and safety limits, which I explain at the start. A safe, affirming space.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="lgbtiq-section lgbtiq-services">
          <div className="lgbtiq-content">
            <h2>How I Can Help</h2>
            <p className="lgbtiq-section-subtitle">Comprehensive support tailored to your unique journey and experiences</p>
            
            <div className="lgbtiq-services-list">
              <div className="lgbtiq-service-item">
                <span className="lgbtiq-service-icon">✓</span>
                <div>
                  <h4>Identity Exploration & Coming Out</h4>
                  <p>Questioning your gender or sexuality? Thinking about coming out? Let's explore your identity at your pace, without pressure or assumptions about who you "should" be.</p>
                </div>
              </div>

              <div className="lgbtiq-service-item">
                <span className="lgbtiq-service-icon">✓</span>
                <div>
                  <h4>Anxiety, Depression & Minority Stress</h4>
                  <p>When the world feels unsafe or unwelcoming, anxiety and depression can take hold. I understand it's not just "in your head" - it's a response to real challenges you face.</p>
                </div>
              </div>

              <div className="lgbtiq-service-item">
                <span className="lgbtiq-service-icon">✓</span>
                <div>
                  <h4>Substance Use & Party Culture</h4>
                  <p>From casual use to dependency, chemsex to self-medication: non-judgmental, evidence-based support that understands why substance use happens in our community.</p>
                </div>
              </div>

              <div className="lgbtiq-service-item">
                <span className="lgbtiq-service-icon">✓</span>
                <div>
                  <h4>Relationships & Family Rejection</h4>
                  <p>Navigating polyamory, coming out to family, chosen family dynamics, or healing from rejection. All relationship structures and families are valid here.</p>
                </div>
              </div>

              <div className="lgbtiq-service-item">
                <span className="lgbtiq-service-icon">✓</span>
                <div>
                  <h4>Trauma, Discrimination & PTSD</h4>
                  <p>Healing from hate crimes, conversion practices, bullying, family rejection, or ongoing discrimination. Your trauma is valid, and recovery is possible.</p>
                </div>
              </div>

              <div className="lgbtiq-service-item">
                <span className="lgbtiq-service-icon">✓</span>
                <div>
                  <h4>Gender Dysphoria & Transition Support</h4>
                  <p>Support throughout your transition journey, exploring gender, navigating medical processes, or managing dysphoria. Your gender is valid, whatever it is.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="lgbtiq-section lgbtiq-cta">
          <div className="lgbtiq-cta-card">
            <h2>Ready to Start Your Journey?</h2>
            <p>Book a confidential telehealth session and take the first step towards affirming, compassionate support.</p>
            <div className="lgbtiq-cta-buttons">
              <Link to="/form/booking" className="lgbtiq-btn-primary">
                Book Appointment
              </Link>
              <a href="mailto:contact@clinicalminds.net" className="lgbtiq-btn-secondary">
                Email Us
              </a>
            </div>
            <div className="lgbtiq-cta-info">
              <p><strong>Medicare Rebates Available</strong></p>
              <p>Clinical Psychologist (Registrar) · AHPRA PSY0002785698</p>
            </div>
          </div>
        </section>

        {/* Confidentiality Note */}
        <section className="lgbtiq-section lgbtiq-note">
          <div className="lgbtiq-content">
            <div className="lgbtiq-note-box">
              <p><strong>Your Privacy & Safety Matters</strong></p>
              <p><strong>Confidentiality:</strong> What you share stays between us, within the usual legal and safety limits. I won't out you to anyone, including family, employers or health providers, without your explicit consent.</p>
              <p><strong>Discreet Billing:</strong> Medicare and private health claims are processed with discretion. You control what information appears on statements.</p>
              <p><strong>Safe Telehealth:</strong> Secure, encrypted video sessions. Access support from wherever you feel safe, whether that's your home, car, or a private space.</p>
              <p>You deserve to be seen, heard, and supported exactly as you are, without fear, judgment, or compromise.</p>
            </div>
          </div>
        </section>

      </div>

    </div>
  );
}

export default LGBTIQ;
